import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import eiaService from '../../services/eiaService';
import DatePickerField from '../../components/common/DatePickerField';
import '../monitoring/Monitoring.css';

const FREQUENCY_OPTIONS = ['daily', 'weekly', 'monthly', 'quarterly', 'event_based'];
const SEVERITY_OPTIONS = ['low', 'medium', 'high', 'critical'];
const THRESHOLD_TYPES = ['max', 'min', 'range'];

const emptyConditionForm = {
  category: '',
  frequency: 'monthly',
  severity: 'medium',
  thresholdType: 'max',
  limit: '',
  min: '',
  max: '',
  unit: '',
};

function describeRule(ruleDefinition) {
  if (!ruleDefinition) return '—';
  const { threshold_type: type, limit, min, max, unit } = ruleDefinition;
  const suffix = unit ? ` ${unit}` : '';
  if (type === 'max') return `≤ ${limit}${suffix}`;
  if (type === 'min') return `≥ ${limit}${suffix}`;
  if (type === 'range') return `${min} - ${max}${suffix}`;
  return '—';
}

const EIACertificateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);

  const [showConditionModal, setShowConditionModal] = useState(false);
  const [editingConditionId, setEditingConditionId] = useState(null);
  const [conditionForm, setConditionForm] = useState(emptyConditionForm);
  const [savingCondition, setSavingCondition] = useState(false);

  const [generatingForId, setGeneratingForId] = useState(null);
  const [taskRange, setTaskRange] = useState({ startDate: '', endDate: '' });
  const [generating, setGenerating] = useState(false);

  const fetchCertificate = useCallback(async () => {
    try {
      setLoading(true);
      const data = await eiaService.getCertificateById(id);
      setCertificate(data.certificate);
    } catch (error) {
      console.error('Failed to load EIA certificate:', error);
      toast.error(error.response?.data?.error || 'Failed to load EIA certificate');
      navigate('/eia/certificates');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchCertificate(); }, [fetchCertificate]);

  const handleStatusChange = async (e) => {
    const status = e.target.value;
    try {
      setSavingStatus(true);
      const { certificate: updated } = await eiaService.updateCertificate(id, { status });
      setCertificate(updated);
      toast.success('Certificate status updated');
    } catch (error) {
      console.error('Failed to update certificate status:', error);
      toast.error(error.response?.data?.error || 'Failed to update certificate status');
    } finally {
      setSavingStatus(false);
    }
  };

  const openAddCondition = () => {
    setEditingConditionId(null);
    setConditionForm(emptyConditionForm);
    setShowConditionModal(true);
  };

  const openEditCondition = (condition) => {
    const rule = condition.ruleDefinition || {};
    setEditingConditionId(condition.id);
    setConditionForm({
      category: condition.category,
      frequency: condition.frequency,
      severity: condition.severity,
      thresholdType: rule.threshold_type || 'max',
      limit: rule.limit ?? '',
      min: rule.min ?? '',
      max: rule.max ?? '',
      unit: rule.unit || '',
    });
    setShowConditionModal(true);
  };

  const handleConditionFieldChange = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    setConditionForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleConditionSubmit = async (e) => {
    e.preventDefault();
    if (!conditionForm.category || !conditionForm.frequency) {
      toast.error('Category and frequency are required');
      return;
    }
    const ruleDefinition = {
      thresholdType: conditionForm.thresholdType,
      unit: conditionForm.unit || undefined,
      ...(conditionForm.thresholdType === 'range'
        ? { min: conditionForm.min === '' ? undefined : Number(conditionForm.min), max: conditionForm.max === '' ? undefined : Number(conditionForm.max) }
        : { limit: conditionForm.limit === '' ? undefined : Number(conditionForm.limit) }),
    };

    try {
      setSavingCondition(true);
      const payload = {
        category: conditionForm.category,
        frequency: conditionForm.frequency,
        severity: conditionForm.severity,
        ruleDefinition,
      };
      if (editingConditionId) {
        await eiaService.updateCondition(editingConditionId, payload);
        toast.success('Condition updated successfully');
      } else {
        await eiaService.createCondition(id, payload);
        toast.success('Condition added successfully');
      }
      setShowConditionModal(false);
      fetchCertificate();
    } catch (error) {
      console.error('Failed to save EIA condition:', error);
      toast.error(error.response?.data?.error || 'Failed to save EIA condition');
    } finally {
      setSavingCondition(false);
    }
  };

  const handleDeleteCondition = async (condition) => {
    if (!window.confirm(`Delete the "${condition.category}" condition? This also removes any of its pending monitoring tasks.`)) return;
    try {
      await eiaService.deleteCondition(condition.id);
      toast.success('Condition deleted');
      fetchCertificate();
    } catch (error) {
      console.error('Failed to delete EIA condition:', error);
      toast.error(error.response?.data?.error || 'Failed to delete EIA condition');
    }
  };

  const openGenerateTasks = (conditionId) => {
    setGeneratingForId(conditionId);
    const today = new Date().toISOString().slice(0, 10);
    const inOneYear = new Date();
    inOneYear.setFullYear(inOneYear.getFullYear() + 1);
    setTaskRange({ startDate: today, endDate: inOneYear.toISOString().slice(0, 10) });
  };

  const handleGenerateTasks = async (e) => {
    e.preventDefault();
    if (!taskRange.startDate || !taskRange.endDate) {
      toast.error('Start and end dates are required');
      return;
    }
    try {
      setGenerating(true);
      const result = await eiaService.generateTasks(generatingForId, taskRange);
      toast.success(result.message || 'Monitoring tasks generated');
      setGeneratingForId(null);
    } catch (error) {
      console.error('Failed to generate monitoring tasks:', error);
      toast.error(error.response?.data?.error || 'Failed to generate monitoring tasks');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="monitoring-page">
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading EIA certificate...</h3>
        </div>
      </div>
    );
  }

  if (!certificate) return null;

  return (
    <div className="monitoring-page">
      <div className="monitoring-header">
        <div>
          <Link to="/eia/certificates" style={{ color: '#6b7280', fontSize: '0.85rem', textDecoration: 'none' }}>
            <i className="fas fa-arrow-left"></i> Back to EIA Certificates
          </Link>
          <h1 style={{ marginTop: '0.5rem' }}><i className="fas fa-file-contract"></i> {certificate.certificateNumber}</h1>
          <p className="header-subtitle">
            {certificate.project?.name || 'Project'} · {certificate.issuingAuthority} · {certificate.countryCode}
          </p>
        </div>
        <select className="filter-select" value={certificate.status} onChange={handleStatusChange} disabled={savingStatus}>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon"><i className="fas fa-calendar-check"></i></div>
          <div><div className="stat-label">Approval Date</div><div className="stat-value" style={{ fontSize: '1rem' }}>{certificate.approvalDate ? new Date(certificate.approvalDate).toLocaleDateString() : '—'}</div></div>
        </div>
        <div className="stat-card warning">
          <div className="stat-icon"><i className="fas fa-calendar-times"></i></div>
          <div><div className="stat-label">Expiry Date</div><div className="stat-value" style={{ fontSize: '1rem' }}>{certificate.expiryDate ? new Date(certificate.expiryDate).toLocaleDateString() : '—'}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-cubes"></i></div>
          <div><div className="stat-label">Components</div><div className="stat-value">{certificate.components?.length || 0}</div></div>
        </div>
        <div className="stat-card success">
          <div className="stat-icon"><i className="fas fa-list-check"></i></div>
          <div><div className="stat-label">Conditions</div><div className="stat-value">{certificate.conditions?.length || 0}</div></div>
        </div>
      </div>

      <div className="category-section" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '1.25rem' }}>
        <div className="monitoring-header" style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}><i className="fas fa-list-check"></i> Conditions</h3>
          <button className="btn-primary-green" onClick={openAddCondition}>
            <i className="fas fa-plus"></i> Add Condition
          </button>
        </div>

        {(certificate.conditions || []).length === 0 ? (
          <p style={{ color: '#6b7280' }}>No conditions added yet - add what this certificate requires you to monitor (e.g. noise, water quality, waste).</p>
        ) : (
          <table className="parameter-table">
            <thead>
              <tr>
                <th>Category</th><th>Frequency</th><th>Severity</th><th>Rule</th><th></th>
              </tr>
            </thead>
            <tbody>
              {certificate.conditions.map((condition) => (
                <tr key={condition.id}>
                  <td style={{ textTransform: 'capitalize' }}>{condition.category}</td>
                  <td>{condition.frequency.replace('_', ' ')}</td>
                  <td><span className={`status-badge status-${condition.severity === 'critical' || condition.severity === 'high' ? 'non_compliant' : 'pending'}`}>{condition.severity}</span></td>
                  <td>{describeRule(condition.ruleDefinition)}</td>
                  <td style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    <button className="btn-icon" title="Generate monitoring tasks" onClick={() => openGenerateTasks(condition.id)}>
                      <i className="fas fa-calendar-plus"></i>
                    </button>
                    <button className="btn-icon" title="Edit" onClick={() => openEditCondition(condition)}>
                      <i className="fas fa-edit"></i>
                    </button>
                    <button className="btn-icon danger" title="Delete" onClick={() => handleDeleteCondition(condition)}>
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Condition Modal */}
      {showConditionModal && (
        <div className="modal-overlay" onClick={() => setShowConditionModal(false)}>
          <div className="monitoring-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-list-check"></i> {editingConditionId ? 'Edit Condition' : 'Add Condition'}</h2>
              <button className="close-btn" onClick={() => setShowConditionModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleConditionSubmit}>
              <div className="modal-content">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Category *</label>
                    <input
                      type="text"
                      value={conditionForm.category}
                      onChange={handleConditionFieldChange('category')}
                      placeholder="e.g. noise, water, air, waste"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Frequency *</label>
                    <select value={conditionForm.frequency} onChange={handleConditionFieldChange('frequency')} required>
                      {FREQUENCY_OPTIONS.map((f) => <option key={f} value={f}>{f.replace('_', ' ')}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Severity</label>
                    <select value={conditionForm.severity} onChange={handleConditionFieldChange('severity')}>
                      {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Threshold Type *</label>
                    <select value={conditionForm.thresholdType} onChange={handleConditionFieldChange('thresholdType')} required>
                      {THRESHOLD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  {conditionForm.thresholdType === 'range' ? (
                    <>
                      <div className="form-group">
                        <label>Min *</label>
                        <input type="number" step="any" value={conditionForm.min} onChange={handleConditionFieldChange('min')} required />
                      </div>
                      <div className="form-group">
                        <label>Max *</label>
                        <input type="number" step="any" value={conditionForm.max} onChange={handleConditionFieldChange('max')} required />
                      </div>
                    </>
                  ) : (
                    <div className="form-group">
                      <label>Limit *</label>
                      <input type="number" step="any" value={conditionForm.limit} onChange={handleConditionFieldChange('limit')} required />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Unit</label>
                    <input
                      type="text"
                      value={conditionForm.unit}
                      onChange={handleConditionFieldChange('unit')}
                      placeholder="e.g. dB, mg/L"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowConditionModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary-green" disabled={savingCondition}>
                  {savingCondition ? 'Saving...' : editingConditionId ? 'Save Changes' : 'Add Condition'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Tasks Modal */}
      {generatingForId && (
        <div className="modal-overlay" onClick={() => setGeneratingForId(null)}>
          <div className="monitoring-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h2><i className="fas fa-calendar-plus"></i> Generate Monitoring Tasks</h2>
              <button className="close-btn" onClick={() => setGeneratingForId(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleGenerateTasks}>
              <div className="modal-content">
                <p style={{ color: '#6b7280', marginTop: 0 }}>
                  Creates one recurring task per linked component for every due date in this range, based on the
                  condition's frequency.
                </p>
                <div className="form-group full-width">
                  <label>Start Date *</label>
                  <DatePickerField
                    value={taskRange.startDate}
                    onChange={(v) => setTaskRange((prev) => ({ ...prev, startDate: v }))}
                    required
                  />
                </div>
                <div className="form-group full-width">
                  <label>End Date *</label>
                  <DatePickerField
                    value={taskRange.endDate}
                    onChange={(v) => setTaskRange((prev) => ({ ...prev, endDate: v }))}
                    minDate={taskRange.startDate}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setGeneratingForId(null)}>Cancel</button>
                <button type="submit" className="btn-primary-green" disabled={generating}>
                  {generating ? 'Generating...' : 'Generate Tasks'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EIACertificateDetail;
